/**
 * @author harish.r <harish.r@314ecorp.com>
 * @description Identity Verification
 */
import { useEffect, useState } from 'react';
import log from 'loglevel';
import _ from 'lodash';
import { Stripe, loadStripe } from '@stripe/stripe-js';
import { Button, Col, App, Row, Space, Typography, message, Tooltip, ButtonProps } from 'antd';
import { BaseType } from 'antd/lib/typography/Base';
import { CheckCircleOutlined, ExclamationCircleOutlined, WarningFilled } from '@ant-design/icons';
import { Help } from '@icon-park/react';

import VerifyNpiOffline from './VerifyNpiOffline';
import useProviderInfo from 'hooks/admin/useProviderInfo';
import useCreateVerificationSession from 'hooks/admin/useCreateVerificationSession';
import useUpdateVerificationSession from 'hooks/admin/mutate/useUpdateVerificationSession';
import { isValidNpi } from 'utils/admin/npi';
import { useAppStoreValues } from 'store/app';

export interface IProps {
	entity?: Provider.IResponseModel | Provider.IPaginatedResponseModel;
	autoRefetch?: boolean;
	forOnboarding?: boolean;
	forDropdown?: boolean;
	onComplete?: VoidFunction;
	onVerificationSuccess?: (entity: Provider.IResponseModel) => void;
	buttonProps?: ButtonProps;
	forPayerTable?: boolean;
	verifyOffline?: boolean;
}

const querykeysForVerificationStatus = 'identity-verification';

const warningMessage =
	'A Provider linked to the NPI will be available for Patient Eligibility or Claim status checks only after the Identity Verification process has been completed.';

const EKYC_STATUS_CODES: Record<Provider.IeKYCStatus, Provider.IeKYCStatus> = {
	pending: 'pending',
	failed: 'failed',
	in_progress: 'in_progress',
	verified: 'verified',
	verified_offline: 'verified_offline',
	verified_transitive: 'verified_transitive',
	verified_ba: 'verified_ba',
	canceled: 'canceled',
};

const statusMapping = {
	pending: {
		code: EKYC_STATUS_CODES.pending,
		textColor: 'danger' as BaseType,
		tagColor: 'error',
		title: 'Verification Required',
		description: warningMessage,
		showActionButton: true,
		cardButtonText: 'Verify Identity',
		dropdownButtonText: 'Verify',
		workflowButtonText: 'Agree and Continue',
		icon: <ExclamationCircleOutlined />,
	},
	failed: {
		code: EKYC_STATUS_CODES.failed,
		textColor: 'danger' as BaseType,
		tagColor: 'error',
		title: 'Verification Required',
		description: warningMessage,
		showActionButton: true,
		cardButtonText: 'Retry',
		dropdownButtonText: 'Retry',
		workflowButtonText: 'Retry',
		icon: <ExclamationCircleOutlined />,
	},
	in_progress: {
		code: EKYC_STATUS_CODES.in_progress,
		textColor: 'warning' as BaseType,
		tagColor: 'warning',
		title: 'In progress',
		cardButtonText: 'In Progress',
		dropdownButtonText: 'In Progress',
		workflowButtonText: 'In Progress',
		icon: <WarningFilled />,
	},
	verified: {
		code: EKYC_STATUS_CODES.verified,
		textColor: 'success' as BaseType,
		tagColor: 'success',
		title: 'Verified',
		cardButtonText: 'Verify Identity',
		dropdownButtonText: 'Verify',
		workflowButtonText: 'Agree and Continue',
		icon: <CheckCircleOutlined />,
	},
	verified_offline: {
		code: EKYC_STATUS_CODES.verified_offline,
		textColor: 'success' as BaseType,
		tagColor: 'success',
		title: 'Verified',
		cardButtonText: 'Verify Identity',
		dropdownButtonText: 'Verify',
		workflowButtonText: 'Agree and Continue',
		icon: <CheckCircleOutlined />,
	},
	verified_transitive: {
		code: EKYC_STATUS_CODES.verified_transitive,
		textColor: 'success' as BaseType,
		tagColor: 'success',
		title: 'Verified',
		cardButtonText: 'Verify Identity',
		dropdownButtonText: 'Verify',
		workflowButtonText: 'Agree and Continue',
		icon: <CheckCircleOutlined />,
	},
	verified_ba: {
		code: EKYC_STATUS_CODES.verified_ba,
		textColor: 'success' as BaseType,
		tagColor: 'success',
		title: 'Auto Verified',
		cardButtonText: 'Verify Identity',
		dropdownButtonText: 'Verify',
		workflowButtonText: 'Agree and Continue',
		icon: <CheckCircleOutlined />,
	},
	unknown: {
		code: 'unknown' as Provider.IeKYCStatus,
		textColor: 'secondary' as BaseType,
		tagColor: 'processing',
		title: 'Unknown',
		description: warningMessage,
		cardButtonText: 'Verify Identity',
		dropdownButtonText: 'Verify',
		workflowButtonText: 'Agree and Continue',
		icon: <ExclamationCircleOutlined />,
	},
	loading: {
		code: 'loading' as Provider.IeKYCStatus,
		textColor: 'secondary' as BaseType,
		tagColor: 'processing',
		title: 'Loading...!',
		cardButtonText: 'Verify Identity',
		dropdownButtonText: 'Verify',
		workflowButtonText: 'Agree and Continue',
		icon: <ExclamationCircleOutlined />,
	},
};

/**
 * Identity Verification component
 * @param {IProps} props props
 * @returns {React.FC} IdentityVerification
 */
const IdentityVerification: React.FC<IProps> = ({
	entity,
	autoRefetch,
	forOnboarding,
	forDropdown,
	onComplete,
	onVerificationSuccess,
	buttonProps,
	forPayerTable,
	verifyOffline,
}) => {
	const { modal } = App.useApp();
	const { setupInfo } = useAppStoreValues();
	const [stripe, setStripe] = useState<Stripe | null>();
	const npi = entity?.npi || '';

	const { data, isLoading } = useProviderInfo(
		entity?.id || '',
		{ enabled: !!autoRefetch && !!entity?.id, refetchInterval: autoRefetch ? 15000 : false },
		querykeysForVerificationStatus,
	);
	const { mutate: createEkycSession, isPending: isCreatingSession } = useCreateVerificationSession();
	const { mutate: updateEkycSession, isPending: isUpdatingSession } = useUpdateVerificationSession();

	const model = { ...(entity || {}), ...(data || {}) };
	const status = model?.ekycDetails?.verificationStatus ?? statusMapping.unknown.code;
	const isVerified =
		status === statusMapping.verified.code ||
		status === statusMapping.verified_offline.code ||
		status === statusMapping.verified_transitive.code;
	const isInProgress = status === statusMapping.in_progress.code;
	const isFailed = status === statusMapping.failed.code;
	const isPending = status === statusMapping.pending.code;

	const meta: Partial<typeof statusMapping.pending> = isLoading
		? statusMapping.loading
		: _.get(statusMapping, status, statusMapping.unknown);

	/**
	 * load stripe instance
	 */
	const loadStripeInstance = async () => {
		if (setupInfo.stripeIdentityToken) {
			await loadStripe(setupInfo.stripeIdentityToken)
				.then((instance) => setStripe(instance))
				.catch(() => log.error('Failed to load stripe instance.'));
		}
		//
		else {
			log.error('Stripe token not found');
		}
	};

	useEffect(() => {
		loadStripeInstance();
	}, []);

	useEffect(() => {
		if (isVerified && _.isFunction(onVerificationSuccess)) {
			onVerificationSuccess(model as Provider.IResponseModel);
		}
	}, [status]);

	/**
	 * start stripe session
	 */
	const startStripeSessions = async (clientSecret: string) => {
		if (stripe) {
			const { error } = await stripe.verifyIdentity(clientSecret);
			// user cancelled the session
			if ((error?.type as string) === 'user_action') {
				await message.error('Verification session terminated');
			}
			// session failed by technical error
			else if (error) {
				await message.error('Encountered some error during verification, please try again.');
			}
			// success
			else {
				await message.info('Thankyou for completing the verification process. It will take few minutes to reflect here.');

				updateEkycSession(
					{ providerId: entity?.id },
					{
						onSuccess: () => {
							if (_.isFunction(onComplete)) {
								onComplete();
							}
						},
					},
				);
			}
		}
		// stripe not loaded
		else {
			await message.error('Oops! Something went wrong.');
			log.error('Stripe instance not found.');
		}
	};

	const getErrorList = (msgs: string[]) => {
		return (
			<Row gutter={[0, 10]}>
				{_.map(msgs, (msg, index) => (
					<Col key={index} span={24}>
						{msg}
					</Col>
				))}
			</Row>
		);
	};

	/**
	 * validate npi linking & fetch client secret
	 */
	const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
		e.preventDefault();
		if (isValidNpi(npi)) {
			createEkycSession({ providerId: entity?.id }, { onSuccess: (clientSecret) => startStripeSessions(clientSecret) });
		} else {
			modal.error({
				width: 550,
				closable: true,
				title: 'Invalid NPI number',
				content: 'To initiate the Identity Verification process, please add a valid NPI first.',
			});
		}
	};

	const getStatusText = () => {
		if (isFailed) {
			return 'Required';
		}
		if (isPending) {
			return null;
		}
		return meta.title;
	};

	if (forOnboarding) {
		return (
			<Button {...buttonProps} loading={isInProgress || isCreatingSession || isUpdatingSession} onClick={handleClick}>
				{meta.workflowButtonText || statusMapping.unknown.workflowButtonText}
			</Button>
		);
	}

	if (forPayerTable) {
		return (
			<Button {...buttonProps} loading={isCreatingSession || isUpdatingSession} onClick={handleClick}>
				{statusMapping.pending.dropdownButtonText}
			</Button>
		);
	}

	if (forDropdown && meta.showActionButton) {
		return (
			<Button {...buttonProps} loading={isCreatingSession || isUpdatingSession} onClick={handleClick}>
				{meta.dropdownButtonText || statusMapping.unknown.dropdownButtonText}
			</Button>
		);
	}

	if (forDropdown && !meta.showActionButton) {
		return (
			<Typography.Text type={meta.textColor}>
				<Space>
					{meta.icon}
					{meta.title}
				</Space>
			</Typography.Text>
		);
	}

	return (
		<Row gutter={[0, 10]}>
			<Col span={24}>
				<Typography.Text type={meta.textColor}>
					<Space>
						{!isPending ? meta.icon : null}
						{getStatusText()}
						{isFailed && (
							<>
								{'(Last attempt: Failed)'}
								<Tooltip title={() => getErrorList(data?.ekycDetails?.errorMessage ?? [])}>
									<Button type='link' className='p-0.5'>
										<Help theme='filled' fill='#8C8C8C' />
									</Button>
								</Tooltip>
							</>
						)}
					</Space>
				</Typography.Text>
				{meta.showActionButton && (
					<Space>
						{verifyOffline && <VerifyNpiOffline entity={data} style={{ fontSize: 13, marginLeft: 12 }} />}
						<Button
							size='small'
							type='link'
							{...buttonProps}
							loading={isCreatingSession || isUpdatingSession}
							onClick={handleClick}
						>
							{meta.cardButtonText || statusMapping.unknown.cardButtonText}
						</Button>
					</Space>
				)}
			</Col>
		</Row>
	);
};

export { statusMapping };
export default IdentityVerification;
